import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, orderBy } from "firebase/firestore";
import { Search, Package, ArrowUpRight, ArrowDownLeft, Layers, Filter } from 'lucide-react';
import Spinner from './Spinner.js';

const Inventory = ({ db }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');

  useEffect(() => {
    // Subscribing to the 'products' collection
    const q = query(collection(db, "products"), orderBy("sku", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(list);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch = 
        p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'All' || p.type === filterType;

      return matchesSearch && matchesType;
    });
  }, [products, searchTerm, filterType]);

  const productTypes = useMemo(() => {
    const types = new Set(products.map(p => p.type).filter(Boolean));
    return ['All', ...Array.from(types)];
  }, [products]);

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: '#111827' }}>Inventory Master</h1>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>Centralized stock tracking for all parts, presets and consumables</p>
        </div>
      </div>

      {/* SUMMARY STATS (Placeholders) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '25px' }}>
        <StatCard 
          title="Total SKUs" 
          value={products.length} 
          icon={<Package size={20} color="#2563eb" />} 
          color="#eff6ff"
        />
        <StatCard 
          title="Saleable Qty" 
          value="0" 
          subValue="Placeholder"
          icon={<ArrowUpRight size={20} color="#10b981" />} 
          color="#ecfdf5"
        />
        <StatCard 
          title="Rental Qty" 
          value="0" 
          subValue="Placeholder"
          icon={<ArrowDownLeft size={20} color="#f59e0b" />} 
          color="#fffbeb"
        />
        <StatCard 
          title="Active On Site" 
          value="0" 
          subValue="Placeholder"
          icon={<Layers size={20} color="#6366f1" />} 
          color="#f5f3ff"
        />
      </div>

      {/* FILTERS BAR */}
      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1, minWidth: '300px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input 
            type="text" 
            placeholder="Search by SKU, Item Name or Description..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            style={selectStyle}
          >
            {productTypes.map(t => (
              <option key={t} value={t}>{t === 'All' ? 'All Categories' : t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
          
          {/* Placeholder for Warehouse Filter */}
          <select disabled style={{ ...selectStyle, backgroundColor: '#f9fafb', cursor: 'not-allowed' }}>
            <option>All Warehouses (Soon)</option>
          </select>
        </div>
      </div>

      {/* INVENTORY TABLE */}
      <div style={tableContainerStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={theadStyle}>
              <th style={thStyle}>SKU & Category</th>
              <th style={thStyle}>Item Description</th>
              <th style={thStyle}>UOM</th>
              <th style={thStyle}>Saleable</th>
              <th style={thStyle}>Rental</th>
              <th style={thStyle}>Reserved</th>
              <th style={thStyle}>Total Stock</th>
              <th style={thStyle}>Last Update</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>🔍</div>
                  No matching items found in the product database.
                </td>
              </tr>
            ) : (
              filteredProducts.map((p) => (
                <tr 
                  key={p.id} 
                  style={trStyle} 
                  className="hover-highlight"
                >
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '700', color: '#111827' }}>{p.sku}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', marginTop: '2px' }}>
                      {p.type || 'Standard'}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, maxWidth: '300px' }}>
                    <div style={{ fontWeight: '500', color: '#374151' }}>{p.name}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {p.description || '---'}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={uomBadgeStyle}>{p.uom || 'nos'}</span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '600' }}>{p.salesQty || 0}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '600', color: '#2563eb' }}>{p.rentalQty || 0}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ color: '#f59e0b' }}>{p.reservedQty || 0}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '800' }}>{(Number(p.salesQty || 0) + Number(p.rentalQty || 0))}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {p.updatedAt?.seconds ? new Date(p.updatedAt.seconds * 1000).toLocaleDateString() : 'Never'}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <div style={{ color: '#e5e7eb' }}>---</div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- STYLES ---

const StatCard = ({ title, value, subValue, icon, color }) => (
  <div style={{ 
    backgroundColor: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center', gap: '15px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  }}>
    <div style={{ backgroundColor: color, padding: '12px', borderRadius: '10px' }}>{icon}</div>
    <div>
      <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>{title}</div>
      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', lineHeight: '1.2' }}>{value}</div>
      {subValue && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{subValue}</div>}
    </div>
  </div>
);

const filterBarStyle = {
  display: 'flex', gap: '15px', marginBottom: '20px', backgroundColor: 'white', 
  padding: '16px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', flexWrap: 'wrap',
  border: '1px solid #e5e7eb'
};

const inputStyle = {
  width: '100%', padding: '10px 12px 10px 40px', borderRadius: '8px', 
  border: '1px solid #d1d5db', outline: 'none', fontSize: '14px', transition: 'border-color 0.2s'
};

const selectStyle = {
  padding: '10px 16px', borderRadius: '8px', border: '1px solid #d1d5db', 
  outline: 'none', minWidth: '160px', fontSize: '14px', backgroundColor: 'white', cursor: 'pointer'
};

const tableContainerStyle = {
  backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', 
  overflow: 'hidden', border: '1px solid #e5e7eb'
};

const theadStyle = {
  backgroundColor: '#f9fafb', borderBottom: '2px solid #edf2f7'
};

const thStyle = {
  padding: '16px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', 
  color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em'
};

const trStyle = {
  borderBottom: '1px solid #f3f4f6', transition: 'background-color 0.2s'
};

const tdStyle = {
  padding: '14px 20px', fontSize: '14px', verticalAlign: 'middle', color: '#374151'
};

const uomBadgeStyle = {
  backgroundColor: '#f3f4f6', color: '#4b5563', padding: '3px 8px', borderRadius: '4px',
  fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase'
};

const iconBtnStyle = {
  background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '8px',
  borderRadius: '6px', display: 'inline-flex', alignItems: 'center', transition: 'all 0.2s'
};

export default Inventory;